import { useState } from 'react';
import { Icon } from '../../components/ui/Icon';
import { RoleDataPreview } from './RoleDataPreview';
import type {
  User,
  Role,
  PermissionMatrix,
  Suite,
  PermLevel,
} from '../../data/types/platform';

export interface AdminUsersProps {
  users: User[];
  roles: Role[];
  perms: PermissionMatrix;
  suites: Suite[];
  query: string;
  setQuery: (q: string) => void;
  onPick: (u: User) => void;
}

function lvlLabel(lvl: PermLevel | null | undefined): string {
  if (lvl === 'admin') return 'ניהול';
  if (lvl === 'edit')  return 'עריכה';
  if (lvl === 'view')  return 'צפייה';
  return '—';
}

export function AdminUsers({
  users,
  roles,
  perms,
  suites,
  query,
  setQuery,
  onPick,
}: AdminUsersProps) {
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRoleId, setPreviewRoleId] = useState<string>(roles[0]?.id ?? '');

  const filtered = users.filter((u) => {
    if (query && !(u.name.includes(query) || u.email.includes(query) || u.team.includes(query))) return false;
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (statusFilter !== 'all' && u.status !== statusFilter) return false;
    return true;
  });

  return (
    <div>
      <div className="admin-toolbar">
        <div className="admin-search">
          <Icon name="search" size={14} />
          <input
            type="text"
            placeholder="חפש שם, אימייל או צוות…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select
          className="admin-select"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">כל התפקידים</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>{r.he}</option>
          ))}
        </select>
        <select
          className="admin-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">כל הסטטוסים</option>
          <option value="active">פעילים</option>
          <option value="pending">ממתינים</option>
          <option value="inactive">לא פעילים</option>
        </select>
        <button
          className="admin-btn ghost"
          type="button"
          onClick={() => setPreviewOpen(true)}
          title="פתח תצוגה של גישה לפי תפקיד"
        >
          <Icon name="eye" size={14} />
          תצוגה מקדימה של תפקיד
        </button>
        <span className="admin-results-meta">{filtered.length} מתוך {users.length}</span>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 32 }} />
              <th>שם</th>
              <th>תפקיד</th>
              <th>צוות / מחוז</th>
              <th>מערכות בגישה</th>
              <th>אימות</th>
              <th>פעילות אחרונה</th>
              <th>סטטוס</th>
              <th style={{ width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const role = roles.find((r) => r.id === u.role);
              const userPerms = perms[u.role] || {};
              const systems = suites.filter((s) => userPerms[s.id]);
              return (
                <tr key={u.id} onClick={() => onPick(u)}>
                  <td className="user-avatar-cell">
                    <span className="user-avatar">{u.initials}</span>
                  </td>
                  <td>
                    <div className="user-name">
                      {u.name}
                      {u.you && <span className="badge-you">את/ה</span>}
                    </div>
                    <div className="user-email">{u.email}</div>
                  </td>
                  <td>
                    <span className={'role-pill role-' + u.role}>
                      <span className="role-pill-short">{role?.short}</span>
                      {role?.he}
                    </span>
                  </td>
                  <td>
                    <div className="cell-strong">{u.team}</div>
                    {u.region !== '—' && <div className="cell-dim">{u.region}</div>}
                  </td>
                  <td>
                    <div className="systems-strip">
                      {systems.length === 0 && <span className="cell-dim">—</span>}
                      {systems.slice(0, 6).map((s) => {
                        const lvl = userPerms[s.id] ?? null;
                        return (
                          <span
                            key={s.id}
                            className={'sys-chip lvl-' + lvl}
                            title={`${s.he} · ${lvlLabel(lvl)}`}
                          >
                            <Icon name={s.icon} size={11} />
                          </span>
                        );
                      })}
                      {role?.scope === 'all' && <span className="sys-all">הכל</span>}
                    </div>
                  </td>
                  <td>
                    {u.mfa ? (
                      <span className="mfa-on"><Icon name="shield-check" size={12} /> דו-שלבי</span>
                    ) : (
                      <span className="mfa-off"><Icon name="shield-alert" size={12} /> סיסמה בלבד</span>
                    )}
                  </td>
                  <td className="cell-mono">{u.last}</td>
                  <td>
                    <span className={'status-pill status-' + u.status}>
                      <span className="status-dot" />
                      {u.status === 'active' ? 'פעיל' : u.status === 'pending' ? 'ממתין' : 'לא פעיל'}
                    </span>
                  </td>
                  <td className="row-action-cell">
                    <button
                      className="admin-row-action"
                      title="עוד"
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Icon name="more-horizontal" size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {previewOpen && (
        <>
          <div className="drawer-scrim" onClick={() => setPreviewOpen(false)} />
          <aside className="user-drawer role-preview-drawer">
            <div className="drawer-head">
              <div className="drawer-head-text">
                <div className="drawer-name">תצוגה מקדימה של תפקיד</div>
                <div className="drawer-meta">בחר תפקיד כדי לראות את מערכות הגישה, המודולים והאזורים שלו</div>
              </div>
              <button
                className="drawer-close"
                onClick={() => setPreviewOpen(false)}
                aria-label="סגור"
                type="button"
              >
                <Icon name="x" size={16} />
              </button>
            </div>
            <div className="drawer-body">
              <div className="drawer-block">
                <div className="drawer-block-head">בחירת תפקיד</div>
                <select
                  className="admin-select"
                  value={previewRoleId}
                  onChange={(e) => setPreviewRoleId(e.target.value)}
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>{r.he}</option>
                  ))}
                </select>
              </div>
              <RoleDataPreview roleId={previewRoleId} regions={['all']} />
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
